import { useState } from "react";
import { PasswordInput } from "@/components/shop/PasswordInput";
import { Navigate, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RegisterForm } from "@/components/shop/RegisterForm";

const Field = ({ label, ...props }) => (
  <label className="block space-y-1.5">
    <span className="text-sm text-stone-600">{label}</span>
    {props.type === "password" ? <PasswordInput {...props} className="h-12 w-full rounded-xl border border-black/10 bg-[#FFFBF3] px-4 text-sm outline-none transition-colors focus:border-amber-500/60" /> : <input {...props} className="h-12 w-full rounded-xl border border-black/10 bg-[#FFFBF3] px-4 text-sm outline-none transition-colors focus:border-amber-500/60" />}
  </label>
);

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const rawNext = params.get("next") || "/account";
  const next = rawNext.startsWith("/accedi") ? "/account" : rawNext;
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={next} replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (mode, body) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`/auth/${mode}`, body);
      setUser(data);
      toast.success(mode === "login" ? "Bentornato!" : data.b2b_pending ? "Account creato! La richiesta B2B è in verifica." : "Account creato!");
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  const login = (e) => { e.preventDefault(); submit("login", form); };

  const google = () => {
    sessionStorage.setItem("mr_next", next);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const btn = "h-12 w-full rounded-full bg-amber-500 font-display font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60";

  return (
    <div className="mx-auto flex max-w-lg flex-col px-4 py-16">
      <h1 className="font-display text-4xl font-bold tracking-tight">Benvenuto in <span className="text-amber-600">Card Source</span></h1>
      <p className="mt-2 text-stone-600">Accedi per completare gli ordini e salvare i tuoi preferiti.</p>
      <div className="mt-8 rounded-2xl border border-black/10 bg-[#FFFFFF] p-6">
        <button onClick={google} data-testid="google-login-button" className="flex h-12 w-full items-center justify-center gap-3 rounded-full border-2 border-black bg-white font-semibold text-stone-900 shadow-[3px_3px_0_#000] transition-transform hover:scale-[1.01]">
          <svg viewBox="0 0 48 48" className="h-5 w-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          Continua con Google
        </button>
        <div className="my-6 flex items-center gap-3 text-xs text-stone-500"><span className="h-px flex-1 bg-black/10" />oppure<span className="h-px flex-1 bg-black/10" /></div>
        <Tabs defaultValue={params.get("tab") === "registrati" ? "register" : "login"}>
          <TabsList className="grid w-full grid-cols-2 bg-[#FFFBF3]">
            <TabsTrigger value="login" data-testid="tab-login" onClick={() => setError("")}>Accedi</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register" onClick={() => setError("")}>Registrati</TabsTrigger>
          </TabsList>
          {error && <p className="mt-4 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-600" data-testid="auth-error">{error}</p>}
          <TabsContent value="login">
            <form onSubmit={login} className="mt-4 space-y-4">
              <Field label="Email" type="email" required value={form.email} onChange={set("email")} data-testid="login-email-input" />
              <Field label="Password" type="password" required value={form.password} onChange={set("password")} data-testid="login-password-input" />
              <Link to="/password-dimenticata" className="block text-right text-sm text-amber-700 hover:underline" data-testid="forgot-password-link">Password dimenticata?</Link>
              <button disabled={loading} className={btn} data-testid="login-submit-button">{loading ? "Accesso..." : "Accedi"}</button>
            </form>
          </TabsContent>
          <TabsContent value="register">
            <RegisterForm initialType={params.get("tipo") === "azienda" ? "azienda" : "privato"} loading={loading} setError={setError} onSubmit={(body) => submit("register", body)} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
